import { Form, Input, Select, DatePicker, Row, Col } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { PatientSex } from '@/models/patient';
import styles from './AssignPatientModal.module.css';

const { TextArea } = Input;

export interface CreatePatientFormValues {
  firstName: string;
  lastName: string;
  medicalRecordNumber: string;
  dateOfBirth: Dayjs;
  sex?: PatientSex;
  referringPhysicianFirstName?: string;
  referringPhysicianLastName?: string;
  phoneNumber?: string;
  address?: string;
  ethnicity?: string;
  notes?: string;
}

const SEX_OPTIONS: { label: string; value: PatientSex }[] = [
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
  { label: 'Other', value: 'OTHER' },
];

interface CreateNewTabProps {
  form: ReturnType<typeof Form.useForm<CreatePatientFormValues>>[0];
  onSubmit: (values: CreatePatientFormValues) => void;
}

/**
 * Create-patient form embedded inside the AssignPatientModal. The submit is
 * driven by the parent modal's footer (parent calls `form.submit()`); this
 * component just renders fields and forwards `onFinish`.
 *
 * Field schema mirrors src/features/patient-list/NewPatientModal so the two
 * surfaces stay consistent. If you change one, change the other.
 */
export default function CreateNewTab({ form, onSubmit }: CreateNewTabProps) {
  return (
    <Form<CreatePatientFormValues>
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      className={styles.formGrid}
      requiredMark="optional"
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="First Name"
            name="firstName"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'First name is required',
              },
            ]}
          >
            <Input placeholder="e.g. Jane" autoComplete="off" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Last name is required',
              },
            ]}
          >
            <Input placeholder="e.g. Doe" autoComplete="off" />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label="MRN"
            name="medicalRecordNumber"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'MRN is required',
              },
            ]}
          >
            <Input placeholder="e.g. 1234567" autoComplete="off" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="DOB"
            name="dateOfBirth"
            rules={[{ required: true, message: 'DOB is required' }]}
          >
            <DatePicker
              className={styles.fullWidth}
              format="YYYY-MM-DD"
              placeholder="YYYY-MM-DD"
              disabledDate={(d) => !!d && d.isAfter(dayjs().endOf('day'))}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="Sex" name="sex">
            <Select allowClear options={SEX_OPTIONS} placeholder="Select" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Phone Number" name="phoneNumber">
            <Input placeholder="e.g. (555) 123-4567" autoComplete="off" />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label="Referring Physician First Name"
            name="referringPhysicianFirstName"
          >
            <Input placeholder="e.g. Jane" autoComplete="off" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Referring Physician Last Name"
            name="referringPhysicianLastName"
          >
            <Input placeholder="e.g. Smith" autoComplete="off" />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="Ethnicity" name="ethnicity">
            <Input placeholder="e.g. Hispanic or Latino" autoComplete="off" />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item label="Address" name="address">
            <Input
              placeholder="e.g. 123 Main St, Toronto, ON M5V 2A1"
              autoComplete="off"
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item label="Notes" name="notes">
            <TextArea
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder="Add any notes about this patient…"
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}
